import { useQuery } from "@tanstack/react-query";
import { GymsService } from "@/api/gyms";
import type {
  GymsResponse,
  GymDisciplinesResponse,
  GymExercisesResponse,
  GymResourceStatus,
} from "@/api/gyms/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./constants";

interface UseGymsOptions {
  enabled?: boolean;
}

interface UseGymDisciplinesOptions {
  enabled?: boolean;
}

interface UseGymExercisesOptions {
  enabled?: boolean;
}

export const useGyms = (options: UseGymsOptions = {}) => {
  return useQuery<GymsResponse, Error>({
    queryKey: queryKeys.gyms.all,
    queryFn: () => GymsService.instance.getGyms(),
    ...options,
  });
};

export const useGymDisciplines = (
  id: number | undefined,
  status: GymResourceStatus,
  options?: UseGymDisciplinesOptions,
) => {
  return useQuery<GymDisciplinesResponse, Error>({
    queryKey: queryKeys.disciplines.byGym(id!, status),
    queryFn: () => GymsService.instance.getGymDisciplines(id!, status),
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useUpdateGymDisciplines = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      gymId,
      disciplineIds,
    }: {
      gymId: number;
      disciplineIds: number[];
    }) => {
      return GymsService.instance.updateGymDisciplines(gymId, disciplineIds);
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.disciplines.byGym(variables.gymId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.disciplines.all,
      });
    },
  });
};

export const useGymExercises = (
  id: number | undefined,
  status: GymResourceStatus,
  options?: UseGymExercisesOptions,
) => {
  return useQuery<GymExercisesResponse, Error>({
    queryKey: queryKeys.gymExercises.byGym(id!, status),
    queryFn: () => GymsService.instance.getGymExercises(id!, status),
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useUpdateGymExercises = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      gymId,
      exerciseIds,
    }: {
      gymId: number;
      exerciseIds: number[];
    }) => {
      return GymsService.instance.updateGymExercises(gymId, exerciseIds);
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.gymExercises.byGym(variables.gymId),
      });
    },
  });
};
